import { useAtomCallback } from 'jotai/utils';
import { useCallback } from 'react';
import type { LocalAudioTrack as TwilioLocalAudioTrack } from 'twilio-video';

import { logger } from '../../datadog/logger';
import { CallbackError, CallbackSuccess } from '../../types';
import { getStoredAudioVideoSettings, localAudioContextAtom, useStoredAudioVideoSettings } from '../../video-sdk';
import {
  disableNoiseCancellation as disableNoiseCancellation_,
  enableNoiseCancellation as enableNoiseCancellation_,
  getTwilioTrackFromDelegator,
} from '../../video-sdk/twilio';
import { NoiseCancellationCallbackResponseCode } from './useNoiseCancellationCallbacks';

type NoiseCancellationCallbackResponse =
  | CallbackSuccess<TwilioLocalAudioTrack, NoiseCancellationCallbackResponseCode>
  | CallbackSuccess<null, NoiseCancellationCallbackResponseCode.NOOP>
  | CallbackError<NoiseCancellationCallbackResponseCode>;

export function useTwilioNoiseCancellationCallbacks() {
  const getLocalAudioTrack = useAtomCallback(
    useCallback((get) => getTwilioTrackFromDelegator(get(localAudioContextAtom).delegator), []),
  );
  const { setStoredAudioVideoSettings } = useStoredAudioVideoSettings();
  const enableNoiseCancellation = useCallback(
    async (givenTrack?: TwilioLocalAudioTrack): Promise<NoiseCancellationCallbackResponse> => {
      const localAudioTrack = givenTrack ?? getLocalAudioTrack();
      if (!localAudioTrack)
        return {
          status: 'error',
          code: NoiseCancellationCallbackResponseCode.MISSING_TRACK,
        };
      try {
        await enableNoiseCancellation_(localAudioTrack as TwilioLocalAudioTrack);
      } catch (e) {
        const error = e as Error;
        logger.error('Failed to enable noise cancellation', {}, error);
        return {
          status: 'error',
          code: NoiseCancellationCallbackResponseCode.ERROR,
          value: error,
        };
      }
      setStoredAudioVideoSettings((prev) => ({
        ...prev,
        noiseCancellationIsEnabled: true,
      }));
      return {
        status: 'success',
        value: localAudioTrack as TwilioLocalAudioTrack,
      };
    },
    [getLocalAudioTrack, setStoredAudioVideoSettings],
  );

  const disableNoiseCancellation = useCallback(
    async (givenTrack?: TwilioLocalAudioTrack): Promise<NoiseCancellationCallbackResponse> => {
      const localAudioTrack = givenTrack ?? getLocalAudioTrack();
      if (!localAudioTrack)
        return {
          status: 'error',
          code: NoiseCancellationCallbackResponseCode.MISSING_TRACK,
        };
      try {
        await disableNoiseCancellation_(localAudioTrack as TwilioLocalAudioTrack);
      } catch (e) {
        const error = e as Error;
        logger.error('Failed to disable noise cancellation', {}, error);
        return {
          status: 'error',
          code: NoiseCancellationCallbackResponseCode.ERROR,
          value: error,
        };
      }
      setStoredAudioVideoSettings((prev) => ({
        ...prev,
        noiseCancellationIsEnabled: false,
      }));
      return {
        status: 'success',
        value: localAudioTrack as TwilioLocalAudioTrack,
      };
    },
    [getLocalAudioTrack, setStoredAudioVideoSettings],
  );

  const initNoiseCancellation = useCallback(
    async (track?: TwilioLocalAudioTrack): Promise<NoiseCancellationCallbackResponse> => {
      const { noiseCancellationIsEnabled } = getStoredAudioVideoSettings();
      if (noiseCancellationIsEnabled) {
        return await enableNoiseCancellation(track);
      }
      return await disableNoiseCancellation(track);
    },
    [enableNoiseCancellation, disableNoiseCancellation],
  );

  return {
    initNoiseCancellation,
    enableNoiseCancellation,
    disableNoiseCancellation,
  };
}
